import React from "react";
import PropTypes from "prop-types";
import { MicroImage } from "./MicroImage";
import { MicroTooltip } from "./MicroTooltip";
import { MicroControls } from "./MicroControls";
import { BalanceTubeModal } from "./BalanceTubeModal";
import { MicroControlsModal } from "./MicroControlsModal";
import { FRONTVIEW_SLOT_INDICES } from "./Microcentrifuge.utils";
import { CentrifugeContext } from "./index";

export function Microcentrifuge(props) {
  const { isNotRunning } = React.useContext(CentrifugeContext);

  const toggleIsOpen = () => isNotRunning(() => props.toggleLid(!props.isOpen));

  const toggleShowControlsModal = () =>
    props.toggleShowControlsModal(!props.showControlsModal);

  const displayTooltipControls = () => props.toggleShowTooltipControls(true);

  const hideTooltipControls = () => props.toggleShowTooltipControls(false);

  const toggleShowBalanceTubeModal = () =>
    isNotRunning(() =>
      props.toggleShowBalanceTubeModal(!props.showBalanceTubeModal)
    );

  const setFrontViewSlotsList = () =>
    props.slots.reduce((slotList, slot, key) => {
      if (FRONTVIEW_SLOT_INDICES.includes(key)) {
        slotList.push({ ...slot, id: key });
      }
      return slotList;
    }, []);

  return (
    <div
      className="microcentrifuge"
      onMouseLeave={hideTooltipControls}
      onMouseEnter={displayTooltipControls}
    >
      <MicroTooltip
        showControls={props.showTooltipControls}
        slots={props.slots}
        isOpen={props.isOpen}
        openShowControlsModal={toggleShowControlsModal}
        openBalanceModal={toggleShowBalanceTubeModal}
        toggleLid={toggleIsOpen}
        onEjectTube={props.onEjectTube}
        onEjectRotor={props.onEjectRotor}
      />
      <MicroControls
        isOpen={props.isOpen}
        toggleLid={toggleIsOpen}
        slots={setFrontViewSlotsList()}
        openControls={toggleShowControlsModal}
        openBalanceTubeModal={toggleShowBalanceTubeModal}
        onInsertItem={props.onInsertItem}
      />
      <MicroImage isOpen={props.isOpen} />
      <MicroControlsModal
        setRPM={props.setRPM}
        running={props.running}
        rpmValue={props.rpmValue}
        balanced={props.balanced}
        timeValue={props.timeValue}
        isOpen={props.showControlsModal}
        setTimeValue={props.setTimeValue}
        onClose={toggleShowControlsModal}
        runCentrifuge={props.runCentrifuge}
        runInterval={props.defaultRunInterval}
      />
      <BalanceTubeModal
        isOpen={props.showBalanceTubeModal}
        onClose={toggleShowBalanceTubeModal}
        slots={props.slots}
        balanced={props.balanced}
        onTubeDrop={props.onTubeDrop}
      />
    </div>
  );
}

Microcentrifuge.propTypes = {
  /**
   * Boolean to set if apparatus is open
   */
  isOpen: PropTypes.bool,
  /**
   * Boolean to set if digital contols modal should be displayed
   */
  showControlsModal: PropTypes.bool,
  /**
   * Boolean to set if tooltip controls should be displayed
   */
  showTooltipControls: PropTypes.bool,
  /**
   * Boolean to set if Balace Tubes Modal is displayed
   */
  showBalanceTubeModal: PropTypes.bool,
  /**
   * Function to open/close the apparatus lid
   */
  toggleLid: PropTypes.func,
  /**
   * Function to open/close the digital controls modal
   */
  toggleShowControlsModal: PropTypes.func,
  /**
   * Function to open/close the tooltip controls menus
   */
  toggleShowTooltipControls: PropTypes.func,
  /**
   * Function to open/close balance tubes modal
   */
  toggleShowBalanceTubeModal: PropTypes.func,
  /**
   * List of tubes in apparatus with its status.
   */
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of Tube placed in slot
       */
      tubeType: PropTypes.string,
      /**
       * boolean to specify if solution placed
       */
      solution: PropTypes.bool,
      /**
       * Boolean to specify if rotor placed
       */
      rotor: PropTypes.bool,
    })
  ),
  /**
   * Funciton to eject all tubes
   */
  onEjectTube: PropTypes.func,
  /**
   * Function to eject all rotors
   */
  onEjectRotor: PropTypes.func,
  /**
   * Boolean to set if the Microcentrifuge tubes are balanced
   */
  balanced: PropTypes.bool,
  /**
   * boolean to set if Microcentrifuge is running
   */
  running: PropTypes.bool,
  /**
   * Current rpm value set
   */
  rpmValue: PropTypes.number,
  /**
   * Currently set centrifuge runtime
   */
  timeValue: PropTypes.instanceOf(Date),
  /**
   * Function to set RPM value
   */
  setRPM: PropTypes.func,
  /**
   * Function to set Run Time
   */
  setTimeValue: PropTypes.func,
  /**
   * Function to invoke when to  run the Centrifuge
   */
  runCentrifuge: PropTypes.func,
  /**
   * Function to invoke when solution tube is dropped
   */
  onTubeDrop: PropTypes.func,
  /**
   * Function to insert item into centrifuge
   */
  onInsertItem: PropTypes.func,
};

Microcentrifuge.defaultProps = {
  defaultRunInterval: 500,
};
